import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { Fragment, memo, useState, useCallback } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import FormGenerality from './form';
import {
  NewPersonVM,
  ParentVM,
  SearchPersonVM,
} from 'shell/components/shared/sipo-person-form/new-person';
import SearchPerson from '../shared/search-person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { ResponseAnprData } from 'shell/models/person';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

const PaternityComponent = (): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const formContext = useFormikContext<NewPersonVM>();

  const [, getPerson] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    AccordionGeneralityForm | undefined
  >(`${__PERSON_NEW_API_URL__}/persona`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => {
      const subjects = response.data.payload?.risposta3002OK?.listaSoggetti.datiSoggetto || [];

      const [subject] = subjects;

      const generalityAnpr = transformGenerality(subject || {}) || undefined;

      return generalityAnpr;
    },
  });

  const onSearchSearchPerson = useEventCallback(() => {
    const data = {
      criteriRicerca: {
        codiceFiscale: formContext.values.paternity?.searchPerson?.fiscalCode || undefined,
        nome: formContext.values.paternity?.searchPerson?.name || undefined,
        cognome: formContext.values.paternity?.searchPerson?.surname || undefined,
        senzaCognome:
          formContext.values.paternity?.searchPerson?.withoutSurname ||
          !formContext.values.paternity?.searchPerson?.surname
            ? '1'
            : undefined,
        senzaNome:
          formContext.values.paternity?.searchPerson?.withoutName ||
          !formContext.values.paternity?.searchPerson?.name
            ? '1'
            : undefined,
      },
      datiRichiesta: {
        ricercaNazionale: '1',
      },
    };

    (
      getPerson({ data }) as ResponsePromise<
        HttpResponse<ResponseAnprData>,
        AccordionGeneralityForm | undefined
      >
    ).then((response) => {
      const responseCasted = response as AccordionGeneralityForm | undefined;

      formContext.setFieldValue('paternity', {
        searchPerson: undefined,
        generality: {
          ...(responseCasted || {}),
        },
        isFromSearchPerson: true,
      });
    });
  });

  const resetForm = useCallback(() => {
    formContext.setValues({
      ...formContext.values,
      paternity: {
        generality: {
          birthDate: null,
          fiscalCode: '',
          foreign: undefined,
          gender: '',
          isForeign: false,
          italian: undefined,
          name: '',
          surname: '',
        },
      },
    });
  }, [formContext.values, formContext.setValues]);

  const onChangeSearchPerson = useEventCallback(
    (key: keyof SearchPersonVM, value: SearchPersonVM[keyof SearchPersonVM]) => {
      formContext.setFieldValue('paternity', {
        ...(formContext.values.paternity || {}),
        searchPerson: {
          ...(formContext.values.paternity?.searchPerson || {}),
          [key]: value,
        },
      });
    }
  );

  const onFieldGeneralityChange = useEventCallback(
    (
      key: keyof AccordionGeneralityForm,
      value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
    ) => {
      formContext.setFieldValue('paternity', {
        ...(formContext.values.paternity || {}),
        generality: {
          ...(formContext.values.paternity?.generality || {}),
          [key]: value,
        },
      });
    }
  );

  const onFieldsGeneralityChange = useEventCallback((values: AccordionGeneralityForm) => {
    formContext.setFieldValue('paternity', {
      ...(formContext.values.paternity || {}),
      generality: {
        ...(formContext.values.paternity?.generality || {}),
        ...values,
      },
    });
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-paternity-content"
          id="panel-paternity-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Paternità</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SearchPerson
            values={formContext.values.paternity?.searchPerson}
            onChange={onChangeSearchPerson}
            onSearch={onSearchSearchPerson}
            resetForm={resetForm}
          />
          <FormGenerality
            onChangeGenerality={onFieldGeneralityChange}
            onChangeFieldsGenerality={onFieldsGeneralityChange}
            generality={formContext.values.paternity?.generality || undefined}
            errorsGenerality={
              (formContext.errors.paternity as FormikErrors<ParentVM>)
                ?.generality as FormikErrors<AccordionGeneralityForm>
            }
            touchedGenerality={
              (formContext.touched.paternity as unknown as FormikTouched<ParentVM>)
                ?.generality as unknown as FormikTouched<AccordionGeneralityForm>
            }
            labelMunicipalityItalian="Comune nascita"
            labelProvinceItalian="Provincia nascita"
            disabled={formContext.values.paternity?.isFromSearchPerson || false}
          />
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

PaternityComponent.displayName = 'Accordion Paternity component';

const Paternity = memo(PaternityComponent);

export default Paternity;
